/**
 * Sentence Studio (lesson 10): word-card rails the child taps or drags into
 * order, with the free-ink pad below for the writing step.
 */
import React, { useMemo, useState } from "react";
import WritingFreePad from "../WritingFreePad";
import WritingSketchCanvas from "../WritingSketchCanvas";
import writingLocalStore from "../writingLocalStore";
import { WRITING_L10 } from "../../../../data/writingCurriculum";
import { useDragEngine, useDropTarget } from "../../interaction/useDragEngine";
import { ConfettiBurst } from "../../feedback/Celebration";
import { useBreakpoint } from "../../shell/LearnShell";
import { LEARN } from "../../theme";
import {
  Arena,
  Board,
  Caption,
  CheckCard,
  LetterTile,
  PadDock,
  Panel,
  PictureTile,
  ProgressRing,
  Rail,
  RoundPill,
  SpeakerButton,
  StoryLine,
  WordCard,
  WordSlot,
  capitalize,
  darken,
  seededShuffle,
  splitWords,
  stemTokens,
  useBurst,
  useCompleteOnce,
  useLater,
  useMeasure,
  useShake,
  useTimer,
  wash,
} from "./arenaKit";

const useCompact = () => {
  const bp = useBreakpoint();
  return bp === "xs" || bp === "sm";
};

function DropWordSlot({ registerTarget, id, ...rest }) {
  const ref = useDropTarget(registerTarget, id, null);
  return <WordSlot ref={ref} {...rest} />;
}

/** A word card that is also a drop target (capital / stop / split games). */
function DropWordCard({ registerTarget, id, index, ...rest }) {
  const data = useMemo(() => ({ index }), [index]);
  const ref = useDropTarget(registerTarget, id, data);
  return (
    <div ref={ref} style={{ display: "inline-flex" }}>
      <WordCard {...rest} />
    </div>
  );
}

function FreePadDock({ accent, prompt, doneTitle, onDone, resetKey }) {
  return (
    <PadDock>
      <WritingFreePad resetKey={resetKey} accent={accent} prompt={prompt} doneTitle={doneTitle} onDone={onDone} />
    </PadDock>
  );
}

/** Sentence rendered as a rail of static word cards. */
function SentenceRail({ sentence, accent, highlight, tone = "quiet", size = "sm" }) {
  return (
    <Rail gap={6}>
      {splitWords(sentence).map((w, i) => (
        <WordCard key={`${w}-${i}`} text={w} as="div" accent={accent} size={size} tone={highlight && w.replace(/[.,]/g, "") === highlight ? "selected" : tone} />
      ))}
    </Rail>
  );
}

// ── Pick It, Then Write It ───────────────────────────────────────────────────

export const PickThenWriteGame = ({ activity, profile, speak, sfx, onComplete, reduceMotion }) => {
  const frames = WRITING_L10.pictureFrames;
  const accent = activity.accent;
  const compact = useCompact();
  const [index, setIndex] = useState(0);
  const [choice, setChoice] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();
  const frame = frames[Math.min(index, frames.length - 1)];
  const tokens = useMemo(() => stemTokens(frame.stem), [frame]);
  const sentence = choice ? frame.stem.replace("___", choice.word) : "";

  const pick = (item) => {
    sfx?.("snap");
    setChoice(item);
    speak?.(frame.stem.replace("___", item.word), { interrupt: true, dedupe: false });
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId }) => {
      if (!targetId) return false;
      pick(payload);
      return true;
    },
  });

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <Caption style={{ textAlign: "left" }}>{choice ? "Hear it, then write the word" : "Pick a picture for the blank"}</Caption>
          <RoundPill done={Math.min(index + 1, frames.length)} total={frames.length} accent={accent} />
        </div>
        <Rail gap={8}>
          {tokens.map((tok, i) =>
            tok.blank ? (
              <React.Fragment key={`blank-${i}`}>
                <DropWordSlot
                  registerTarget={registerTarget}
                  id="blank"
                  accent={accent}
                  hover={hoverId === "blank"}
                  active={!choice}
                  reduceMotion={reduceMotion}
                  label="?"
                  ariaLabel={choice ? choice.word : "blank"}
                  onClick={() => choice && speak?.(sentence, { interrupt: true, dedupe: false })}
                >
                  {choice && <WordCard text={choice.word} as="div" accent={accent} tone="selected" pop reduceMotion={reduceMotion} />}
                </DropWordSlot>
                {tok.suffix && <WordCard text={tok.suffix} as="div" accent={accent} tone="quiet" style={{ padding: "8px 12px" }} />}
              </React.Fragment>
            ) : (
              <WordCard key={`${tok.text}-${i}`} text={tok.text} as="div" accent={accent} />
            ),
          )}
          {choice && <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the sentence" onClick={() => speak?.(sentence, { interrupt: true, dedupe: false })} />}
        </Rail>
      </Panel>
      {!choice ? (
        <Board title={activity.title} symbol={activity.symbol} accent={accent}>
          <Rail gap={compact ? 12 : 18}>
            {frame.choices.map((item) => (
              <PictureTile
                key={item.word}
                name={item.assetName}
                alt={item.word}
                label={item.word}
                accent={accent}
                size={compact ? 1.4 : 1.6}
                reduceMotion={reduceMotion}
                ariaLabel={`Choose ${item.word}`}
                drag={dragProps(item, (p) => <PictureTile name={p.assetName} alt={p.word} label={p.word} accent={accent} size={compact ? 1.4 : 1.6} tone="selected" as="div" />)}
                onClick={() => pick(item)}
              />
            ))}
          </Rail>
        </Board>
      ) : (
        <FreePadDock
          accent={accent}
          resetKey={`${index}-${choice.word}`}
          prompt={`Write ${choice.word}`}
          onDone={() => {
            writingLocalStore.saveLesson10Sentence(profile?.id, sentence, index);
            sfx?.("correct");
            fire();
            if (index + 1 >= frames.length) complete();
            else {
              sfx?.("pageturn", { volume: 0.5 });
              setIndex((n) => n + 1);
              setChoice(null);
            }
          }}
        />
      )}
      <GhostLayer />
    </Arena>
  );
};

// ── Frame Fade ───────────────────────────────────────────────────────────────

export const FrameFadeGame = ({ activity, speak, sfx, onComplete, reduceMotion }) => {
  const accent = activity.accent;
  const [stage, setStage] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const model = WRITING_L10.frameFadeModels[Math.min(stage, 2)];
  const tone = ["idle", "faded", "blank"][Math.min(stage, 2)];

  return (
    <Arena>
      <Panel style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <SpeakerButton accent={accent} size={48} reduceMotion={reduceMotion} ariaLabel="Hear the sentence" onClick={() => speak?.(model, { interrupt: true, dedupe: false })} />
        <Rail gap={6} style={{ flex: 1, justifyContent: "flex-start" }}>
          {splitWords(model).map((w, i) => (
            <WordCard key={`${stage}-${i}`} text={w} as="div" accent={accent} tone={tone} reduceMotion={reduceMotion} style={tone === "blank" ? { minWidth: `${Math.max(3, w.length) * 0.75}em` } : undefined} />
          ))}
        </Rail>
        <RoundPill done={Math.min(stage + 1, 3)} total={3} accent={accent} label="Page" />
      </Panel>
      <FreePadDock
        accent={accent}
        resetKey={stage}
        prompt={stage === 2 ? "Write the whole sentence from memory" : "Copy the sentence"}
        onDone={() => {
          sfx?.("correct");
          if (stage + 1 >= 3) complete();
          else {
            sfx?.("pageturn", { volume: 0.5 });
            setStage((n) => n + 1);
          }
        }}
      />
    </Arena>
  );
};

// ── Today's Sentence ─────────────────────────────────────────────────────────

const GOAL_CHECKS = ["Capital start", "Finger spaces", "End mark"];

export const TodaysSentenceGame = ({ activity, speak, sfx, onComplete, reduceMotion }) => {
  const accent = activity.accent;
  const [checks, setChecks] = useState([]);
  const [drafted, setDrafted] = useState(false);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();

  const toggle = (item) => {
    if (checks.includes(item)) return;
    sfx?.("pop", { volume: 0.5 });
    const next = [...checks, item];
    setChecks(next);
    if (next.length >= GOAL_CHECKS.length) {
      sfx?.("correct");
      fire();
      complete();
    }
  };

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={30} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <SpeakerButton accent={accent} size={48} reduceMotion={reduceMotion} ariaLabel="Hear the question" onClick={() => speak?.(WRITING_L10.todaysQuestion, { interrupt: true, dedupe: false })} />
        <div style={{ flex: 1, fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>{WRITING_L10.todaysQuestion}</div>
      </Panel>
      {!drafted ? (
        <FreePadDock accent={accent} prompt="Write your answer in one sentence" onDone={() => { sfx?.("pageturn", { volume: 0.5 }); setDrafted(true); }} />
      ) : (
        <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="Check your sentence" bodyStyle={{ alignItems: "stretch", gap: 10 }}>
          {GOAL_CHECKS.map((item) => (
            <CheckCard key={item} text={item} checked={checks.includes(item)} accent={accent} reduceMotion={reduceMotion} onClick={() => toggle(item)} />
          ))}
        </Board>
      )}
    </Arena>
  );
};

// ── Capital & Stop ───────────────────────────────────────────────────────────

const breakSentence = (sentence) => {
  const words = splitWords(String(sentence).replace(/[.!?]+$/, ""));
  if (words.length) words[0] = words[0].toLowerCase();
  return words;
};

export const CapitalAndStopGame = ({ activity, profile, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const accent = activity.accent;
  const sentences = useMemo(() => {
    const saved = writingLocalStore.lesson10Sentences(profile?.id);
    return WRITING_L10.pictureFrames.map((_, i) => saved[i] || WRITING_L10.capitalStopSentences[i]);
  }, [profile?.id]);
  const [index, setIndex] = useState(0);
  const [marks, setMarks] = useState({ capital: false, stop: false });
  const [picked, setPicked] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();
  const later = useLater();
  const sentence = sentences[Math.min(index, sentences.length - 1)];
  const words = useMemo(() => breakSentence(sentence), [sentence]);
  const last = words.length - 1;
  const fixed = `${words.map((w, i) => (i === 0 ? capitalize(w) : w)).join(" ")}.`;

  const apply = (tool, position) => {
    const ok = tool === "capital" ? position === 0 && !marks.capital : position === last && !marks.stop;
    if (!ok) {
      onWrong?.();
      shake(`w-${position}`);
      return false;
    }
    const next = { ...marks, [tool]: true };
    setMarks(next);
    setPicked(null);
    sfx?.("snap");
    if (next.capital && next.stop) {
      sfx?.("correct");
      fire();
      speak?.(fixed, { interrupt: true, dedupe: false });
      later(() => {
        if (index + 1 >= sentences.length) complete();
        else {
          setIndex((n) => n + 1);
          setMarks({ capital: false, stop: false });
        }
      }, 1100);
    }
    return true;
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId, targetData }) => (targetId ? apply(payload.tool, targetData.index) : false),
  });

  const toolTile = (tool, label, aria) => (
    <LetterTile
      key={tool}
      label={label}
      ariaLabel={aria}
      accent={accent}
      size={0.95}
      fontScale={tool === "stop" ? 0.7 : 0.48}
      tone={marks[tool] ? "blank" : picked === tool ? "selected" : "idle"}
      disabled={marks[tool]}
      reduceMotion={reduceMotion}
      drag={dragProps({ tool }, (p) => <LetterTile label={p.tool === "stop" ? "." : "A"} fontScale={p.tool === "stop" ? 0.7 : 0.48} as="div" accent={accent} size={0.95} tone="selected" />)}
      onClick={() => {
        sfx?.("pop", { volume: 0.5 });
        speak?.(tool === "capital" ? "Capital start." : "End mark.", { interrupt: true, dedupe: false });
        setPicked((p) => (p === tool ? null : tool));
      }}
      style={marks[tool] ? { opacity: 0.35 } : undefined}
    />
  );

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={24} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the sentence" onClick={() => speak?.(fixed, { interrupt: true, dedupe: false })} />
          <RoundPill done={Math.min(index + 1, sentences.length)} total={sentences.length} accent={accent} />
        </div>
        <Rail gap={8}>
          {words.map((w, i) => {
            const isFirst = i === 0;
            const isLast = i === last;
            const shown = isFirst && marks.capital ? capitalize(w) : w;
            const done = (isFirst && marks.capital) || (isLast && marks.stop);
            return (
              <DropWordCard
                key={`${index}-${i}`}
                registerTarget={registerTarget}
                id={`w-${i}`}
                index={i}
                text={isLast && marks.stop ? `${shown}.` : shown}
                ariaLabel={`Word ${shown}`}
                accent={accent}
                tone={hoverId === `w-${i}` ? "hint" : done ? "correct" : isShaking(`w-${i}`) ? "wrong" : "idle"}
                shaking={isShaking(`w-${i}`)}
                pop={done}
                reduceMotion={reduceMotion}
                onClick={() => {
                  if (picked) apply(picked, i);
                  else speak?.(w, { interrupt: true, dedupe: false });
                }}
              />
            );
          })}
        </Rail>
      </Panel>
      <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="Put the capital on the first word and the stop on the last">
        <Rail gap={28}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            {toolTile("capital", "A", "Capital start")}
            <span style={{ fontWeight: 700, fontSize: 15, color: LEARN.color.muted }}>capital</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            {toolTile("stop", ".", "End mark")}
            <span style={{ fontWeight: 700, fontSize: 15, color: LEARN.color.muted }}>stop</span>
          </div>
        </Rail>
      </Board>
      <GhostLayer />
    </Arena>
  );
};

// ── One More ─────────────────────────────────────────────────────────────────

export const OneMoreGame = ({ activity, speak, sfx, onComplete, reduceMotion }) => {
  const accent = activity.accent;
  const complete = useCompleteOnce(onComplete);
  return (
    <Arena>
      <Panel style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <StoryLine text={WRITING_L10.oneMoreStart} accent={accent} tone="selected" badge="1" onClick={() => speak?.(WRITING_L10.oneMoreStart, { interrupt: true, dedupe: false })} ariaLabel="Hear the first sentence" reduceMotion={reduceMotion} />
        <div style={{ display: "flex", alignItems: "center", gap: 10, paddingLeft: 4 }}>
          <span style={{ minWidth: 30, height: 30, borderRadius: 999, background: wash(accent, 0.16), color: darken(accent, 0.15), display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700 }}>2</span>
          <span style={{ fontSize: 20, fontWeight: 700 }}>Then what?</span>
        </div>
      </Panel>
      <FreePadDock
        accent={accent}
        prompt="Write what happened next"
        onDone={() => {
          sfx?.("correct");
          complete();
        }}
      />
    </Arena>
  );
};

// ── Three Ways ───────────────────────────────────────────────────────────────

const THREE_WAY_JOINERS = ["and", "but", "so"];

export const ThreeWaysGame = ({ activity, speak, sfx, onComplete, reduceMotion }) => {
  const accent = activity.accent;
  const kernel = WRITING_L10.threeWaysKernel;
  const [used, setUsed] = useState([]);
  const [current, setCurrent] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();

  const choose = (joiner) => {
    if (used.includes(joiner)) return;
    sfx?.("snap");
    setCurrent(joiner);
    speak?.(`${kernel} ${joiner}...`, { interrupt: true, dedupe: false });
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId }) => {
      if (!targetId) return false;
      choose(payload.joiner);
      return true;
    },
  });

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear the idea" onClick={() => speak?.(current ? `${kernel} ${current}...` : kernel, { interrupt: true, dedupe: false })} />
          <RoundPill done={Math.min(used.length + (current ? 1 : 0), 2)} total={2} accent={accent} label="Ending" />
        </div>
        <Rail gap={6}>
          {splitWords(kernel).map((w, i) => (
            <WordCard key={i} text={w} as="div" accent={accent} size="sm" />
          ))}
          <DropWordSlot
            registerTarget={registerTarget}
            id="joiner"
            accent={accent}
            hover={hoverId === "joiner"}
            active={!current}
            reduceMotion={reduceMotion}
            label="joining word"
            ariaLabel={current || "joining word slot"}
            onClick={() => current && speak?.(`${kernel} ${current}...`, { interrupt: true, dedupe: false })}
          >
            {current && <WordCard text={current} as="div" accent={accent} size="sm" tone="selected" pop reduceMotion={reduceMotion} />}
          </DropWordSlot>
          <WordCard text="..." as="div" accent={accent} size="sm" tone="quiet" />
        </Rail>
      </Panel>
      {used.length >= 2 ? (
        <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="Two different endings are ready">
          <Rail gap={14}>
            {used.map((joiner) => (
              <WordCard key={joiner} text={joiner} size="lg" as="div" accent={accent} tone="correct" />
            ))}
          </Rail>
        </Board>
      ) : !current ? (
        <Board title={activity.title} symbol={activity.symbol} accent={accent} caption={used.length ? "Now pick a different joining word" : "Pick a joining word"}>
          <Rail gap={16}>
            {THREE_WAY_JOINERS.map((joiner) => {
              const done = used.includes(joiner);
              return (
                <WordCard
                  key={joiner}
                  text={joiner}
                  size="lg"
                  accent={accent}
                  tone={done ? "correct" : "idle"}
                  disabled={done}
                  reduceMotion={reduceMotion}
                  ariaLabel={done ? `${joiner}, used` : `Choose ${joiner}`}
                  drag={dragProps({ joiner }, (p) => <WordCard text={p.joiner} size="lg" as="div" accent={accent} tone="selected" />)}
                  onClick={() => {
                    sfx?.("pop", { volume: 0.5 });
                    choose(joiner);
                  }}
                />
              );
            })}
          </Rail>
        </Board>
      ) : (
        <FreePadDock
          accent={accent}
          resetKey={current}
          prompt={`Finish: ${kernel} ${current} …`}
          onDone={() => {
            const next = [...used, current];
            setUsed(next);
            setCurrent(null);
            sfx?.("correct");
            fire();
            if (next.length >= 2) complete();
          }}
        />
      )}
      <GhostLayer />
    </Arena>
  );
};

// ── Join the Sentences · Who & That ──────────────────────────────────────────

const JoinListGame = ({ rounds, activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const accent = activity.accent;
  const [index, setIndex] = useState(0);
  const [joined, setJoined] = useState(false);
  const [picked, setPicked] = useState(null);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();
  const round = rounds[Math.min(index, rounds.length - 1)];
  const pool = useMemo(() => seededShuffle([...new Set(rounds.map((r) => r.joiner))], index + 2), [rounds, index]);

  const tryJoin = (joiner) => {
    if (joined) return false;
    if (joiner !== round.joiner) {
      onWrong?.();
      shake(joiner);
      return false;
    }
    setJoined(true);
    setPicked(null);
    sfx?.("snap");
    sfx?.("correct");
    fire();
    speak?.(round.joined, { interrupt: true, dedupe: false });
    return true;
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ payload, targetId }) => (targetId ? tryJoin(payload.joiner) : false),
  });

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <SpeakerButton
            accent={accent}
            size={44}
            reduceMotion={reduceMotion}
            ariaLabel="Hear the sentences"
            onClick={() => speak?.(joined ? round.joined : `${round.first} ${round.second}`, { interrupt: true, dedupe: false })}
          />
          <RoundPill done={Math.min(index + 1, rounds.length)} total={rounds.length} accent={accent} />
        </div>
        {joined ? (
          <SentenceRail sentence={round.joined} accent={accent} highlight={round.joiner} tone="idle" />
        ) : (
          <>
            <StoryLine text={round.first} accent={accent} badge="1" as="div" />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <DropWordSlot
                registerTarget={registerTarget}
                id="joiner"
                accent={accent}
                hover={hoverId === "joiner"}
                active={!!picked}
                reduceMotion={reduceMotion}
                label="joining word"
                ariaLabel="joining word slot"
                onClick={() => picked && tryJoin(picked)}
              />
            </div>
            <StoryLine text={round.second} accent={accent} badge="2" as="div" />
          </>
        )}
      </Panel>
      {!joined ? (
        <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="Which word joins them?">
          <Rail gap={16}>
            {pool.map((joiner) => (
              <WordCard
                key={`${index}-${joiner}`}
                text={joiner}
                size="lg"
                accent={accent}
                tone={picked === joiner ? "selected" : isShaking(joiner) ? "wrong" : "idle"}
                shaking={isShaking(joiner)}
                reduceMotion={reduceMotion}
                ariaLabel={`Choose ${joiner}`}
                drag={dragProps({ joiner }, (p) => <WordCard text={p.joiner} size="lg" as="div" accent={accent} tone="selected" />)}
                onClick={() => {
                  sfx?.("pop", { volume: 0.5 });
                  speak?.(joiner, { interrupt: true, dedupe: false });
                  setPicked((p) => (p === joiner ? null : joiner));
                }}
              />
            ))}
          </Rail>
        </Board>
      ) : (
        <FreePadDock
          accent={accent}
          resetKey={round.joined}
          prompt="Write the joined sentence"
          onDone={() => {
            sfx?.("correct");
            if (index + 1 >= rounds.length) complete();
            else {
              sfx?.("pageturn", { volume: 0.5 });
              setIndex((n) => n + 1);
              setJoined(false);
            }
          }}
        />
      )}
      <GhostLayer />
    </Arena>
  );
};

export const JoinSentencesGame = (props) => <JoinListGame rounds={WRITING_L10.joinRounds} {...props} />;
export const WhoAndThatGame = (props) => <JoinListGame rounds={WRITING_L10.relativeRounds} {...props} />;

// ── Untangle ─────────────────────────────────────────────────────────────────

export const UntangleGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const accent = activity.accent;
  const words = useMemo(() => splitWords(WRITING_L10.runOn), []);
  const splitAfter = splitWords(WRITING_L10.untangled[0]).length - 1;
  const [split, setSplit] = useState(false);
  const [index, setIndex] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const { shake, isShaking } = useShake();
  const [burst, fire] = useBurst();

  const trySplit = (i) => {
    if (split) return false;
    if (i !== splitAfter) {
      onWrong?.();
      shake(i);
      return false;
    }
    setSplit(true);
    sfx?.("snap");
    sfx?.("correct");
    fire();
    speak?.(WRITING_L10.untangled.join(" "), { interrupt: true, dedupe: false });
    return true;
  };

  const { dragProps, registerTarget, hoverId, GhostLayer } = useDragEngine({
    reduceMotion,
    onPickup: () => sfx?.("pickup", { volume: 0.5 }),
    onDrop: ({ targetId, targetData }) => (targetId ? trySplit(targetData.index) : false),
  });

  const target = WRITING_L10.untangled[Math.min(index, 1)];

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={18} originY={20} reduceMotion={reduceMotion} />
      <Panel style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <SpeakerButton accent={accent} size={44} reduceMotion={reduceMotion} ariaLabel="Hear it" onClick={() => speak?.(split ? WRITING_L10.untangled.join(" ") : WRITING_L10.runOn, { interrupt: true, dedupe: false })} />
          {split && <RoundPill done={Math.min(index + 1, 2)} total={2} accent={accent} label="Sentence" />}
        </div>
        {split ? (
          <>
            {WRITING_L10.untangled.map((s, i) => (
              <StoryLine key={s} text={s} accent={accent} badge={i + 1} tone={i === index ? "selected" : "correct"} as="div" />
            ))}
          </>
        ) : (
          <Rail gap={6}>
            {words.map((w, i) => (
              <DropWordCard
                key={i}
                registerTarget={registerTarget}
                id={`w-${i}`}
                index={i}
                text={w}
                ariaLabel={`Stop after ${w}`}
                accent={accent}
                size="sm"
                tone={hoverId === `w-${i}` ? "hint" : isShaking(i) ? "wrong" : "idle"}
                shaking={isShaking(i)}
                reduceMotion={reduceMotion}
                onClick={() => trySplit(i)}
              />
            ))}
          </Rail>
        )}
      </Panel>
      {!split ? (
        <Board title={activity.title} symbol={activity.symbol} accent={accent} caption="Where does the first idea stop? Tap that word, or drag the stop onto it">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <LetterTile
              label="."
              fontScale={0.7}
              ariaLabel="Stop mark"
              accent={accent}
              size={1.15}
              reduceMotion={reduceMotion}
              drag={dragProps({ tool: "stop" }, () => <LetterTile label="." fontScale={0.7} as="div" accent={accent} size={1.15} tone="selected" />)}
              onClick={() => {
                sfx?.("pop", { volume: 0.5 });
                speak?.("Drop me where the first idea ends.", { interrupt: true, dedupe: false });
              }}
            />
            <span style={{ fontWeight: 700, fontSize: 15, color: LEARN.color.muted }}>stop</span>
          </div>
        </Board>
      ) : (
        <FreePadDock
          accent={accent}
          resetKey={index}
          prompt={`Write: ${target}`}
          onDone={() => {
            sfx?.("correct");
            if (index + 1 >= WRITING_L10.untangled.length) complete();
            else {
              sfx?.("pageturn", { volume: 0.5 });
              setIndex((n) => n + 1);
            }
          }}
        />
      )}
      <GhostLayer />
    </Arena>
  );
};

// ── Picture Sentence ─────────────────────────────────────────────────────────

export const PictureSentenceGame = ({ activity, profile, sfx, onComplete, reduceMotion }) => {
  const accent = activity.accent;
  const compact = useCompact();
  const [strokes, setStrokes] = useState([]);
  const [ref, size] = useMeasure();
  const complete = useCompleteOnce(onComplete);
  const [burst, fire] = useBurst();

  return (
    <Arena>
      <ConfettiBurst key={burst} active={burst > 0} count={20} originY={30} reduceMotion={reduceMotion} />
      <Panel pad={8} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 6px" }}>
          <Caption style={{ textAlign: "left" }}>Draw your picture</Caption>
          <button
            type="button"
            className="wt-focus"
            onClick={() => setStrokes([])}
            disabled={!strokes.length}
            style={{ border: "none", background: "transparent", fontFamily: LEARN.font, fontWeight: 700, fontSize: 14, color: strokes.length ? darken(accent, 0.1) : LEARN.color.muted, cursor: strokes.length ? "pointer" : "default", padding: "4px 8px" }}
          >
            Clear drawing
          </button>
        </div>
        <div ref={ref} style={{ height: compact ? "clamp(120px, 28vh, 180px)" : "clamp(150px, 30vh, 240px)", borderRadius: 18, overflow: "hidden", background: "#fff", border: `2px solid ${wash(accent, 0.3)}` }}>
          {size.width > 1 && size.height > 1 && (
            <WritingSketchCanvas strokes={strokes} onChange={setStrokes} width={size.width} height={size.height} showGuide={false} paper={false} accent={accent} />
          )}
        </div>
      </Panel>
      <FreePadDock
        accent={accent}
        prompt="Write one sentence about your picture"
        doneTitle="Save to My Book"
        onDone={() => {
          writingLocalStore.saveLesson10Picture(profile?.id);
          sfx?.("pageturn", { volume: 0.6 });
          fire();
          complete();
        }}
      />
    </Arena>
  );
};

// ── Word Sprint ──────────────────────────────────────────────────────────────

export const WordSprintGame = ({ activity, speak, sfx, onComplete, reduceMotion }) => {
  const accent = activity.accent;
  const compact = useCompact();
  const seconds = activity.seconds || WRITING_L10.completion.sprintSeconds || 60;
  const [score, setScore] = useState(0);
  const complete = useCompleteOnce(onComplete);
  const pictures = WRITING_L10.sprintPictures;
  const order = useMemo(() => seededShuffle(pictures.map((_, i) => i), 4), [pictures]);
  const current = pictures[order[score % order.length]] || pictures[score % pictures.length];

  const { progress } = useTimer(seconds, () => {
    sfx?.("sparkle");
    complete();
  });

  return (
    <Arena>
      <Panel style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <ProgressRing progress={progress} accent={accent} size={compact ? 64 : 80} label={score} sub={score === 1 ? "word" : "words"} />
        <PictureTile
          name={current.assetName}
          alt={current.word}
          accent={accent}
          size={compact ? 1.2 : 1.5}
          reduceMotion={reduceMotion}
          ariaLabel={`Hear ${current.word}`}
          onClick={() => {
            sfx?.("pop", { volume: 0.4 });
            speak?.(current.word, { interrupt: true, dedupe: false });
          }}
        />
        <Caption style={{ textAlign: "left", flex: 1 }}>Write the picture word. Any number counts.</Caption>
      </Panel>
      <FreePadDock
        accent={accent}
        resetKey={score}
        prompt="Write the picture word"
        doneTitle="Next word"
        onDone={() => {
          sfx?.("pop", { volume: 0.6 });
          setScore((n) => n + 1);
        }}
      />
    </Arena>
  );
};
